import React, { useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { FormSpacer } from 'components';
import { FormStepContentProps } from 'types';
import { FormDataType, useFormState, assistanceList, sourceOfApplicationList } from 'contexts/FormProvider';
import { useScrollTop } from 'hooks/useScrollTop';
import { useCheckWindowCMS } from 'hooks/useCheckWindowCMS';
import { Form } from 'components';
import { useContentContext } from '@qbe/dfc-content';
import validationSchema from './schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormGroupLabel, DatePicker, Button } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import dayjs from 'dayjs';
import { DropdownController, TextFieldController } from 'controllers';
import { useIsFieldRequired } from 'utils/isFieldRequired';
import { useContentComplaintPartners } from 'utils/content';

const TellUsMoreForm = ({ onComplete, onStart }: FormStepContentProps) => {
  useScrollTop();
  useCheckWindowCMS();

  const { formData } = useFormState();
  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });

  const form = useForm<FormDataType>({
    mode: 'onBlur',
    defaultValues: formData,
    resolver: yupResolver(validationSchema) as any,
  });

  const {
    handleSubmit,
    control,
    watch,
    formState: { isDirty },
  } = form;

  useEffect(() => {
    isDirty && onStart();
  }, [isDirty, onStart]);

  const { isFieldRequired } = useIsFieldRequired(form, validationSchema);

  // Container for info box
  const formRef = useRef<HTMLFormElement>(null);

  const isDisabled = !validationSchema.isValidSync(watch());

  return (
    <Form onSubmit={handleSubmit(onComplete)} ref={formRef} data-test="tell-us-more-form">
      <FormCard variant="pageTitle" title={getTextContent('fha.tell.us.more.stepHeader')} description="" />

      <FormCard variant="sectionTitle" title={getTextContent('fha.tell.us.more.hardship.header')}>
        <FormGroupLabel
          alignCenter
          required
          legendElementOveride
          title={getTextContent('fha.tell.us.more.hardship.dateOfApplication')}
        />
        <FormSpacer type="input" />
        <Controller
          name="dateOfApplication"
          control={control}
          render={({ field: { value, ...field }, fieldState: { error } }) => {
            return (
              <DatePicker
                {...field}
                value={dayjs(value as any)}
                id={field.name}
                format="DD/MM/YYYY"
                label="DD/MM/YYYY"
                data-track={field.name}
                data-track-value="mask"
                error={!!error}
                helperText={error?.message ? error.message : null}
                maxDate={dayjs()}
              />
            );
          }}
        />

        <FormGroupLabel
          alignCenter
          required={isFieldRequired('sourceOfApplication')}
          legendElementOveride
          title={getTextContent('fha.tell.us.more.hardship.sourceOfApplication')}
        />
        <FormSpacer type="input" />
        <DropdownController
          control={control}
          label={getTextContent('fha.tell.us.more.hardship.soa.placeholder')}
          name="sourceOfApplication"
          capture
          optionList={sourceOfApplicationList.map((value) => {
            return {
              label: value,
              value,
            };
          })}
        />

        <FormGroupLabel
          alignCenter
          required={isFieldRequired('reason')}
          legendElementOveride
          title={getTextContent('fha.tell.us.more.hardship.reason')}
        />
        <FormSpacer type="input" />
        <TextFieldController
          name="reason"
          control={control}
          multiline
          label={getTextContent('fha.tell.us.more.hardship.reason.placeholder')}
          placeholder={getTextContent('fha.tell.us.more.hardship.reason.placeholder')}
          maxLength={4000}
        />

        <FormGroupLabel
          alignCenter
          required={isFieldRequired('assistance')}
          legendElementOveride
          title={getTextContent('fha.tell.us.more.hardship.assistance')}
        />
        <FormSpacer type="input" />
        <DropdownController
          control={control}
          label={getTextContent('fha.tell.us.more.hardship.assistance.placeholder')}
          name="assistance"
          capture
          optionList={assistanceList.map((value) => {
            return {
              label: value,
              value,
            };
          })}
        />

        <FormGroupLabel
          alignCenter
          required={isFieldRequired('description')}
          legendElementOveride
          title={getTextContent('fha.tell.us.more.hardship.description')}
        />
        <FormSpacer type="input" />
        <TextFieldController
          name="description"
          control={control}
          multiline
          label={getTextContent('fha.tell.us.more.hardship.description.placeholder')}
          placeholder={getTextContent('fha.tell.us.more.hardship.description.placeholder')}
          maxLength={4000}
        />

        <FormSpacer type="formgroup" />

        <Button type="submit" label="Continue" disabled={isDisabled} />
      </FormCard>
    </Form>
  );
};

export default TellUsMoreForm;
